import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { z } from "zod";

import type { RouterContextType } from "@/api/permissions/permissions.api";
import { MODULE_KEYS_MAP } from "@/api/permissions/permissions.types";
import { ReturnsPage } from "@/components/features/Procurement/design/ReturnsPage";
import { useBreadcrumb } from "@/hooks/useBreadcrumb";
import { authGuardWithPermissions } from "@/utils/authGuard";
import { getRouteIcon } from "@/utils/getRouteIconComponent";

/**
 * SERV-2859 AC4.7. Pre-filters the returns screen from the queue's return badge / "Return Items"
 * action, both of which now go to `/procurement/returns?requestId=<id>` instead of a bare route.
 * Mirrors the queue route's own search schema (routes/_authenticated/procurement/index.tsx:16-26).
 */
const procurementReturnsSearchSchema = z.object({
  requestId: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/_authenticated/procurement/returns")({
  component: ProcurementReturnsPage,
  validateSearch: procurementReturnsSearchSchema.parse,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

function ProcurementReturnsPage() {
  const navigate = useNavigate();
  const { requestId } = useSearch({ from: "/_authenticated/procurement/returns" });

  useBreadcrumb([
    {
      title: "Procurement",
      href: "/procurement",
      icon: getRouteIcon(MODULE_KEYS_MAP.procurement),
    },
    { title: "Returns", href: "/procurement/returns" },
  ]);

  return (
    <ReturnsPage
      requestId={requestId}
      onClearRequestFilter={() =>
        void navigate({ to: "/procurement/returns", search: {} as never })
      }
    />
  );
}
